'use client';

import {
  Avatar,
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  Label,
  PlainTooltip,
  SystemIcon,
  SystemIconName,
  Typography,
} from '@wishket/design-system';
import { useRef, useState } from 'react';
import { useClickOutside } from '@wishket/yogokit';

import { AuthorInfoTooltip } from './parts';

import { Link, ShareBox } from '@/shared/Components';
import { useDeviceCheck } from '@/shared/hooks';
import { formatViewCount } from '@/shared/utils';
import { PATH } from '@/shared/constants';

const STATISTICS_CONSTANT_MAP = {
  view: { icon: 'small_eye', label: '조회 수' },
  like: { icon: 'small_like', label: '좋아요' },
  scrap: { icon: 'small_scrap', label: '스크랩' },
};

interface AuthorStatisticsProps {
  type: 'view' | 'like' | 'scrap';
  count: number;
}

const AuthorStatistics = ({ type, count }: AuthorStatisticsProps) => {
  const { icon, label } = STATISTICS_CONSTANT_MAP[type];
  const isView = type === 'view';

  return (
    <Box className="flex items-center gap-1.5">
      <SystemIcon
        name={icon as SystemIconName}
        className="hidden text-w-gray-600 desktop:block"
        solid
      />
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2">
        {isView ? formatViewCount(count) : count.toLocaleString()}
      </Typography>
    </Box>
  );
};

const AuthorColumnStatistics = ({ type, count }: AuthorStatisticsProps) => {
  const { label } = STATISTICS_CONSTANT_MAP[type];
  const isView = type === 'view';

  return (
    <Box className="flex w-full flex-col items-center">
      <Typography variant="body1" className="font-medium text-w-gray-900">
        {isView ? formatViewCount(count) : count.toLocaleString()}
      </Typography>
      <Typography variant="body2" className="text-w-gray-600">
        {label}
      </Typography>
    </Box>
  );
};

interface ButtonBoxProps {
  isMine: boolean;
  isSubscribed: boolean;
  isTooltipOpen: boolean;
  onSubscription: () => void;
  urlIdentifier?: string;
}

const ButtonBox = ({
  isMine,
  isSubscribed,
  isTooltipOpen,
  onSubscription,
  urlIdentifier,
}: ButtonBoxProps) => {
  const [isOpen, setIsOpen] = useState(false);

  if (isMine && urlIdentifier) {
    return (
      <Link
        href={`${PATH.HOME}/@${urlIdentifier}/profiles`}
        className="h-fit w-full desktop:w-fit"
      >
        <Button
          variant="outlined"
          className="w-full gap-1.5 border-primary-200 bg-primary-10 desktop:w-[202px]"
        >
          <SystemIcon name="medium_edit" className="text-primary" />
          <Typography variant="body1">프로필 편집</Typography>
        </Button>
      </Link>
    );
  }

  return (
    <>
      {isSubscribed ? (
        <Box className="w-full desktop:w-fit">
          <Box className="relative">
            <Button
              needThrottle
              variant="outlined"
              className="group relative w-full desktop:w-[202px]"
              onClick={() => setIsOpen(true)}
            >
              <SystemIcon name="small_bell" className="text-primary" solid />
              <Typography variant="body1">알림 받는 중</Typography>
            </Button>
            {isTooltipOpen && <AuthorInfoTooltip />}
            <Dialog
              variant="caution"
              title="알림 취소"
              description={
                '알림을 취소할까요?\n최신 콘텐츠를 이메일로 받을 수 없어요.'
              }
              confirm="취소하기"
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
              onConfirm={() => {
                setIsOpen(false);
                onSubscription();
              }}
            />
          </Box>
        </Box>
      ) : (
        <Button
          variant="solid"
          className="group relative w-full desktop:w-[202px]"
          onClick={onSubscription}
          needThrottle
        >
          <SystemIcon name="small_bell" className="text-w-white" />
          <Typography variant="body1">최신 콘텐츠 알림 받기</Typography>
        </Button>
      )}
    </>
  );
};

interface AuthorInfoCardProps {
  name: string;
  urlIdentifier: string;
  imageUrl: string;
  isCompany: boolean;
  count: {
    view: number;
    like: number;
    scrap: number;
    subscription: number;
  };
  isSubscribed: boolean;
  isTooltipOpen: boolean;
  tagLine: string;
  onSubscription: () => void;
  toggleShareBoxVisible: () => void;
  isShareBoxVisible: boolean;
  isMine: boolean;
  shareTitle: string;
  shareUrl: string;
}

const AuthorInfoCard = ({
  name,
  urlIdentifier,
  imageUrl,
  isCompany,
  tagLine,
  count,
  isSubscribed,
  onSubscription,
  isTooltipOpen,
  toggleShareBoxVisible,
  isShareBoxVisible,
  isMine,
  shareTitle,
  shareUrl,
}: AuthorInfoCardProps) => {
  const { isDesktop } = useDeviceCheck();
  const shareBoxRef = useRef<HTMLDivElement>(null);

  useClickOutside(shareBoxRef, toggleShareBoxVisible);

  return (
    <>
      <Box
        className="flex flex-col gap-6 px-5 grid-in-info desktop:px-0"
        data-testid="feature--author-info"
      >
        <Box className="flex items-center gap-6">
          <Avatar size={isDesktop ? 'xl' : 'lg'} imgUrl={imageUrl} />
          <Box className="flex flex-col gap-3">
            <Box className="flex flex-col gap-1">
              <Box className="flex items-center gap-2">
                <Typography
                  variant="subTitle1"
                  className="font-medium text-w-gray-900 desktop:typo-title3"
                >
                  {name}
                </Typography>
                {isCompany && (
                  <Label.Root
                    type="filled"
                    size="sm"
                    className="h-fit w-[38px]"
                  >
                    <Label.Text>기업</Label.Text>
                  </Label.Root>
                )}
              </Box>
              <Box className="flex items-center gap-2 text-w-gray-700">
                <Typography variant="body1" className="line-clamp-1 break-all">
                  {tagLine}
                </Typography>
                <Divider isVertical className="h-3.5" />
                <Typography variant="body1" className="shrink-0">
                  {count.subscription}명 알림 받는 중
                </Typography>
              </Box>
            </Box>
            <Box className="hidden gap-3 text-w-gray-600 desktop:flex">
              <AuthorStatistics type="view" count={count.view} />
              <AuthorStatistics type="like" count={count.like} />
              <AuthorStatistics type="scrap" count={count.scrap} />
            </Box>
          </Box>
        </Box>
        <Box className="flex gap-6 desktop:hidden">
          <AuthorColumnStatistics type="view" count={count.view} />
          <Divider isVertical className="h-[50px]" />
          <AuthorColumnStatistics type="like" count={count.like} />
          <Divider isVertical className="h-[50px]" />
          <AuthorColumnStatistics type="scrap" count={count.scrap} />
        </Box>
      </Box>
      <Box className="flex w-full justify-end gap-3 px-5 grid-in-action desktop:px-0">
        <ButtonBox
          isMine={isMine}
          isSubscribed={isSubscribed}
          isTooltipOpen={isTooltipOpen}
          onSubscription={onSubscription}
          urlIdentifier={urlIdentifier}
        />

        <Box className="relative h-[50px]">
          <PlainTooltip
            text="공유"
            variant="up"
            className="w-fit"
            tooltipClassName="z-[15]"
          >
            <IconButton size="lg" outline onClick={toggleShareBoxVisible}>
              <SystemIcon name="large_share" className="text-w-gray-600" />
            </IconButton>
          </PlainTooltip>
          {isShareBoxVisible && (
            <ShareBox
              ref={shareBoxRef}
              title={shareTitle}
              url={shareUrl}
              className="absolute right-0 mt-2"
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default AuthorInfoCard;
